import React, {useState} from "react";
import {Building, PhotoModel} from "../../types/protocols";
import {BaseComponentImpl, RoomImpl} from "../../types/implementations";
import BlueprintWithMarkers, {NumberedMarker} from "../../Components/BlueprintWithMarkers";
import ToggleSwitch from "../../Components/ToggleSwitch";
import BuildingCustomButton from "../BuildingDetails/BuildingCustomButton";
import {translate} from "../../types/EnumTranslations";
import {getComponentRoomKey} from "../../Helpers/ComponentRoomUtils";

interface LevelBlueprintSectionProps {
    currentBlueprint: PhotoModel | undefined;
    currentBlueprintKey: string | undefined;
    building: Building;
    rooms: RoomImpl[];
    isNoLevelCase: boolean;
    isAdmin: boolean;
    showRooms: boolean;
    showComponents: boolean;
    onShowRoomsChange: (checked: boolean) => void;
    onShowComponentsChange: (checked: boolean) => void;
    /** Spots of level-mode parking areas placed on this drawing; the toggle is hidden when empty. */
    parkingSpots?: NumberedMarker[];
    showParking?: boolean;
    onShowParkingChange?: (checked: boolean) => void;
    /** The level has at least one lägenhet/lokal; gates the "Visa hyresobjekt" toggle. */
    hasUnits?: boolean;
    /** Pins of those units on this drawing; drawn only while showUnits is on (off by default). */
    unitSpots?: NumberedMarker[];
    showUnits?: boolean;
    onShowUnitsChange?: (checked: boolean) => void;
    onSpotClick?: (spotKey: string, event?: React.MouseEvent) => void;
    componentsForBlueprint: BaseComponentImpl[];
    allComponents: BaseComponentImpl[];
    hoveredRoomKey: string | null;
    hoveredComponentKey: string | null;
    /** Marker clicks pass the event so the page can open a new tab on Cmd/Ctrl/middle-click. */
    onRoomClick: (roomKey: string, event?: React.MouseEvent) => void;
    onRoomMouseEnter: (roomKey: string) => void;
    onRoomMouseLeave: () => void;
    onComponentClick: (componentKey: string, event?: React.MouseEvent) => void;
    /** The component page; the list shown without a drawing links to it. */
    componentPath: (componentKey: string) => string;
    onComponentMouseEnter: (componentKey: string) => void;
    onComponentMouseLeave: () => void;
    onUploadClick: () => void;
    onFileDrop?: (file: File) => void;
    onFileDropError?: (message: string) => void;
}

const LevelBlueprintSection: React.FC<LevelBlueprintSectionProps> = ({
    currentBlueprint,
    currentBlueprintKey,
    building,
    rooms,
    isNoLevelCase,
    isAdmin,
    showRooms,
    showComponents,
    onShowRoomsChange,
    onShowComponentsChange,
    parkingSpots = [],
    showParking = false,
    onShowParkingChange,
    hasUnits = false,
    unitSpots = [],
    showUnits = false,
    onShowUnitsChange,
    onSpotClick,
    componentsForBlueprint,
    allComponents,
    hoveredRoomKey,
    hoveredComponentKey,
    onRoomClick,
    onRoomMouseEnter,
    onRoomMouseLeave,
    onComponentClick,
    componentPath,
    onComponentMouseEnter,
    onComponentMouseLeave,
    onUploadClick,
    onFileDrop,
    onFileDropError,
}) => {
    const hasBlueprint = !isNoLevelCase && currentBlueprint && currentBlueprintKey;
    // Never in the no-level view.
    const hasParking = !isNoLevelCase && parkingSpots.length > 0;
    const hasRentalUnits = !isNoLevelCase && hasUnits;
    const displaySpots = [
        ...(hasParking && showParking ? parkingSpots : []),
        ...(hasRentalUnits && showUnits ? unitSpots : []),
    ];
    const displayComponents = showComponents ? componentsForBlueprint : [];
    const displayComponentsList = showComponents ? allComponents : [];
    const [isDragOver, setIsDragOver] = useState(false);

    const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        e.stopPropagation();
        if (!isNoLevelCase && isAdmin) {
            setIsDragOver(true);
        }
    };

    const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragOver(false);
    };

    const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragOver(false);

        if (isNoLevelCase || !isAdmin) return;

        const files = e.dataTransfer.files;
        if (files.length > 0) {
            const file = files[0];
            const isImage = file.type.startsWith('image/');
            const isPDF = file.type === 'application/pdf';
            if (isImage || isPDF) {
                onFileDrop?.(file);
            } else {
                onFileDropError?.('Endast bilder (PNG, JPG, JPEG) och PDF-filer är tillåtna.');
            }
        }
    };

    return (
        <div>
            {/* Shared title bar */}
            <div className="blueprint-title-container">
                <h4 style={{ margin: 0 }}>Ritning</h4>
                <div className="blueprint-checkboxes">
                    <div className="component-toggle">
                        <label>
                            <ToggleSwitch checked={showRooms} onChange={onShowRoomsChange} />
                            <span>Visa rumsmarkörer</span>
                        </label>
                    </div>
                    <div className="component-toggle">
                        <label>
                            <ToggleSwitch checked={showComponents} onChange={onShowComponentsChange} />
                            <span>Visa komponenter</span>
                        </label>
                    </div>
                    {hasParking && hasBlueprint && (
                        <div className="component-toggle">
                            <label>
                                <ToggleSwitch checked={showParking} onChange={checked => onShowParkingChange?.(checked)} />
                                <span>Visa parkering</span>
                            </label>
                        </div>
                    )}
                    {hasRentalUnits && hasBlueprint && (
                        <div className="component-toggle">
                            <label>
                                <ToggleSwitch checked={showUnits} onChange={checked => onShowUnitsChange?.(checked)} />
                                <span>Visa hyresobjekt</span>
                            </label>
                        </div>
                    )}
                </div>
                {!isNoLevelCase && isAdmin && (
                    <button
                        onClick={onUploadClick}
                        className="blueprint-change-button"
                    >
                        Byt ritning
                    </button>
                )}
            </div>

            {hasBlueprint ? (
                <BlueprintWithMarkers
                    key={`blueprint-${showRooms}-${showComponents}-${displayComponents.length}-${displaySpots.length}`}
                    blueprint={currentBlueprint}
                    currentBlueprintKey={currentBlueprintKey}
                    building={building}
                    rooms={showRooms ? rooms : []}
                    utilities={displayComponents}
                    onRoomClick={onRoomClick}
                    onUtilityClick={onComponentClick}
                    spots={displaySpots}
                    onSpotClick={onSpotClick}
                    title=""
                />
            ) : (
                <>
                    {/* Dashed border placeholder for missing blueprint */}
                    <div
                        className={`blueprint-placeholder ${isDragOver ? 'blueprint-placeholder--drag-over' : ''}`}
                        onDragOver={handleDragOver}
                        onDragLeave={handleDragLeave}
                        onDrop={handleDrop}
                    >
                        <span className="blueprint-placeholder-text">
                            {isDragOver ? 'Släpp för att ladda upp' : 'Ritning saknas'}
                        </span>
                        {!isNoLevelCase && isAdmin && (
                            <>
                                <button
                                    onClick={onUploadClick}
                                    className="blueprint-upload-button"
                                >
                                    Ladda upp
                                </button>
                                <span className="blueprint-placeholder-hint">
                                    eller dra och släpp en fil här
                                </span>
                            </>
                        )}
                    </div>

                    {/* Show components list when blueprint is missing and showComponents is true */}
                    {showComponents && displayComponentsList.length > 0 && (
                        <div style={{marginTop: '20px'}}>
                            <h5 style={{marginBottom: '10px'}}>Komponenter</h5>
                            <div style={{display: 'flex', flexDirection: 'column', gap: '8px'}}>
                                {displayComponentsList.map((component) => (
                                    <BuildingCustomButton
                                        key={component.key}
                                        title={component.displayName(building)}
                                        metadata={[
                                            translate(component.type),
                                            rooms.find(r => r.key === getComponentRoomKey(component))?.displayName()
                                        ].filter((s): s is string => Boolean(s))}
                                        imagePath={`components/${component.type ?? "component_general"}.png`}
                                        to={componentPath(component.key)}
                                        onMouseEnter={() => onComponentMouseEnter(component.key)}
                                        onMouseLeave={onComponentMouseLeave}
                                        isHovered={hoveredComponentKey === component.key}
                                    />
                                ))}
                            </div>
                        </div>
                    )}
                </>
            )}
        </div>
    );
};

export default LevelBlueprintSection;
